import { Injectable } from '@angular/core';
import { APIService } from './api.service';
import { HttpParams } from '@angular/common/http';
import { Paged } from '../interfaces/paged';
import {
  MarketVacancy,
  MarketVacancyInterest,
  MarketVacancyStatistics,
  MarketVacancyTabCounts,
  MarketVacancyFilters,
  MatchingUsersResponse,
  SuggestedVacancy
} from '../interfaces/market-vacancy';

@Injectable({
  providedIn: 'root'
})
export class MarketVacancyService extends APIService {

  /**
   * Get paginated list of market vacancies with filters
   */
  public async getMarketVacancies(options: MarketVacancyFilters = {}, page: number = 1): Promise<Paged<MarketVacancy>> {
    let params = new HttpParams().set('page', page.toString());

    if (options.status !== undefined) {
      params = params.set('status', options.status.toString());
    }
    if (options.source) {
      params = params.set('source', options.source.toString());
    }
    if (options.search) {
      params = params.set('search', options.search);
    }
    if (options.with_interests) {
      params = params.set('with_interests', 'true');
    }
    if (options.sort_by) {
      params = params.set('sort_by', options.sort_by);
    }
    if (options.sort_dir) {
      params = params.set('sort_dir', options.sort_dir);
    }

    return await this.get<Paged<MarketVacancy>>(
      '/market-vacancies',
      'Could not get market vacancies',
      { params }
    );
  }

  /**
   * Search market vacancies with advanced filters and sorting
   */
  public async findMarketVacancies(
    page: number = 1,
    filters: any = null,
    sorts: { col: string; dir: number }[] = [],
    perPage: number = null
  ): Promise<Paged<MarketVacancy>> {
    return await this.post<any, Paged<MarketVacancy>>('/market-vacancies/find', {
      page,
      per_page: perPage,
      sorts,
      filters
    });
  }

  /**
   * Get a single market vacancy by ID. When `forUser` is set, persists the
   * server-side click event with target_user_id = forUser (staffer-on-behalf-of).
   */
  public async getMarketVacancy(id: number, forUser?: number): Promise<MarketVacancy> {
    if (isNaN(id)) {
      return null;
    }
    const options = typeof forUser === 'number'
      ? { params: new HttpParams().set('for_user', forUser.toString()) }
      : undefined;
    return await this.get<MarketVacancy>(
      '/market-vacancies/' + id,
      'Could not find market vacancy: ' + id,
      options
    );
  }

  /**
   * Create a new market vacancy (admins/staffers only)
   */
  public async addMarketVacancy(vacancy: Partial<MarketVacancy>, silentStatuses: number[] = []): Promise<MarketVacancy> {
    return await this.post<Partial<MarketVacancy>, MarketVacancy>(
      '/market-vacancies/',
      vacancy,
      'Market vacancy added!',
      silentStatuses
    );
  }

  /**
   * Update a market vacancy
   */
  public async saveMarketVacancy(
    id: number,
    updates: Partial<MarketVacancy>,
    savemessage: string = 'Market vacancy updated!'
  ): Promise<MarketVacancy> {
    return await this.put<Partial<MarketVacancy>, MarketVacancy>(
      '/market-vacancies/' + id,
      updates,
      savemessage
    );
  }

  /**
   * Delete a market vacancy
   */
  public async deleteMarketVacancy(id: number): Promise<any> {
    return await this.delete<any>(
      '/market-vacancies/' + id,
      'Market vacancy deleted!'
    );
  }

  /**
   * Express interest in a market vacancy. When `forUser` is set, the resulting
   * interest event is tagged with target_user_id = forUser.
   */
  public async expressInterest(vacancyId: number, forUser?: number): Promise<MarketVacancyInterest> {
    const params = typeof forUser === 'number'
      ? new HttpParams().set('for_user', forUser.toString())
      : new HttpParams();
    const qs = params.toString();
    return await this.post<any, MarketVacancyInterest>(
      `/market-vacancies/${vacancyId}/interest${qs ? `?${qs}` : ''}`,
      {},
      'Interest recorded!'
    );
  }

  /**
   * Withdraw interest from a market vacancy
   */
  public async withdrawInterest(vacancyId: number): Promise<any> {
    return await this.delete<any>(
      `/market-vacancies/${vacancyId}/interest`,
      'Interest withdrawn'
    );
  }

  public async hideVacancy(vacancyId: number): Promise<any> {
    return await this.post<any, any>(
      `/market-vacancies/${vacancyId}/hide`,
      {},
      'Vacancy hidden'
    );
  }

  public async unhideVacancy(vacancyId: number): Promise<any> {
    return await this.delete<any>(
      `/market-vacancies/${vacancyId}/hide`,
      'Vacancy restored'
    );
  }

  /**
   * Get current user's interests
   */
  public async getMyInterests(page: number = 1): Promise<Paged<MarketVacancyInterest>> {
    const params = new HttpParams().set('page', page.toString());
    return await this.get<Paged<MarketVacancyInterest>>(
      '/market-vacancies/my-interests',
      'Could not get your interests',
      { params }
    );
  }

  /**
   * Convert a market vacancy to an internal project
   */
  public async convertToProject(vacancyId: number): Promise<any> {
    return await this.post<any, any>(
      `/market-vacancies/${vacancyId}/convert-to-project`,
      {},
      'Market vacancy converted to project!'
    );
  }

  /**
   * Create a CRM opportunity from a market vacancy (admin/staffer only).
   * On success the server returns the newly-created Opportunity object.
   * Throws with a 422 JSON body when the vacancy has no end-client linked.
   */
  public async createOpportunity(vacancyId: number, body: { [k: string]: any } = {}): Promise<any> {
    return await this.post<any, any>(
      `/market-vacancies/${vacancyId}/opportunity`,
      body,
      null,
      [422]
    );
  }

  /**
   * Extract AI suggestions from description text (no vacancy created)
   */
  public async suggestFromDescription(title: string, description: string, company?: string): Promise<any> {
      return await this.post<any, any>(
          '/market-vacancies/suggest-from-description',
          { title, description, company }
      );
  }

  /**
   * Distinct source platforms ever ingested (e.g. Flextender, Magnit,
   * Striive) — for a broker picker that offers known market sources instead
   * of free text.
   */
  public async getSources(): Promise<string[]> {
    const res = await this.get<{ sources: string[] }>(
      '/market-vacancies/sources',
      'Could not get vacancy sources'
    );
    return res?.sources ?? [];
  }

  /**
   * Get statistics about market vacancies
   */
  public async getStatistics(): Promise<MarketVacancyStatistics> {
    return await this.get<MarketVacancyStatistics>(
      '/market-vacancies/statistics',
      'Could not get statistics'
    );
  }

  /**
   * Get matching users for a vacancy based on skills and certifications
   */
  public async getMatchingUsers(vacancyId: number, limit: number = 5): Promise<MatchingUsersResponse> {
    const params = new HttpParams().set('limit', limit.toString());
    return await this.get<MatchingUsersResponse>(
      `/market-vacancies/${vacancyId}/matching-users`,
      'Could not get matching users',
      { params }
    );
  }

  /** Admin only: queue judging for this vacancy's not-yet-checked consultants (1 h cooldown server side). */
  public async checkEligibility(vacancyId: number): Promise<{ queued: number }> {
    return await this.post<{}, { queued: number }>(`/market-vacancies/${vacancyId}/eligibility/check`, {}, null, [403, 429, 500, 502, 503]);
  }

  public async markSeen(vacancyIds: number[]): Promise<{ success: boolean; count: number }> {
    return await this.post('/market-vacancies/mark-seen', { vacancy_ids: vacancyIds });
  }

  public async markAllSeen(): Promise<{ success: boolean; count: number }> {
    return await this.post('/market-vacancies/mark-all-seen', {});
  }

  public async getUnseenCount(): Promise<{ count: number }> {
    return await this.get('/market-vacancies/unseen-count', 'Could not get unseen count');
  }

  public async getTabCounts(): Promise<MarketVacancyTabCounts> {
    return await this.get('/market-vacancies/tab-counts', 'Could not get tab counts');
  }

  /**
   * Get suggested vacancies for a user based on match scores
   */
  public async getSuggestedVacanciesForUser(userId: number): Promise<SuggestedVacancy[]> {
    return await this.get<SuggestedVacancy[]>(
      `/users/${userId}/suggested-vacancies`,
      'Could not get suggested vacancies'
    );
  }
}
